import React, { useLayoutEffect, useState, useEffect, useCallback, useRef } from "react";
import { SelectBox, Template } from "devextreme-react";
import DataGrid, {
  Column,
  Editing,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
} from "devextreme-react/data-grid";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import "./../../../App.css";
import Modal from "react-modal";
import { askConfirmation, showResponseMessage } from "../../../helper/message";
import { categoryService } from "../../../apis/apiServices/Category";
import { subCategoryService } from "../../../apis/apiServices/SubCategory";
import { assetSpecService } from "../../../apis/apiServices/AssetSpec";

/**
 * AssetSpecificationComponent
 *
 * Permission handling is same pattern as CategoryComponent:
 * - Pass a prop `featurePermission` from parent (defaults to {}).
 * - Compute booleans canView / canAdd / canEdit / canDelete and gate UI/actions accordingly.
 *
 * Usage:
 * <AssetSpecificationComponent featurePermission={someFeaturePermissionObject} />
 */
export default function AssetSpecificationComponent(props) {
  // CHANGED: accept props.featurePermission and derive permission booleans (safe defaults)
  const featurePermission = props?.featurePermission || {};
  const canView = !!featurePermission?.canView;
  const canAdd = !!featurePermission?.canAdd;
  const canEdit = !!featurePermission?.canEdit;
  const canDelete = !!featurePermission?.canDelete;

  const user = useSelector((state) => state.user.data);
  const [state, setState] = useState({
    isSubmitted: false,
    isSaveAssetSpecificationModalOpen: false,
    assetSpecId: 0,
    categoryId: 0,
    subCategoryId: 0,
    rowData: null,
    assetSpecificationSubLst: [],
    isViewOnly: false,
  });

  const [errors, setErrors] = useState({
    categoryValidation: "",
    subCategoryValidation: "",
  });

  const [categoryDropdown, setCategoryDropdown] = useState([]);
  const [subCategoryDropdown, setSubCategoryDropdown] = useState([]);
  const [assetSpecficationDataSourceLst, setAssetSpecficationDataSourceLst] = useState([]);

  const refAssetSpecDataGrid = useRef(null);
  const refAssetSpecSubDataGrid = useRef(null);

  useLayoutEffect(() => {
    document.querySelector("#pageTitle").innerHTML = `Asset Specification`;
  }, []);

  // ensure fetch on mount
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    try {
      (async () => {
        if (!mounted) return;
        await fetchApiOnPageLoad();
      })();
    } catch (error) {
      console.log(error);
    }
    return () => {
      mounted = false;
      controller.abort();
    };
  }, []); // run once

  const fetchApiOnPageLoad = useCallback(async () => {
    try {
      // keep as microtask to allow layout to settle (preserves previous behavior)
      setTimeout(async () => {
        await getAssetSpecificationLst();
      }, 0);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const getCategoryDropdown = async () => {
    try {
      const response = await categoryService.getCategoryList();
      setCategoryDropdown(response?.data ?? []);
    } catch (error) {
      console.log(error);
    }
  };

  const getSubCategoryDropdown = async (categoryId) => {
    try {
      const response = await subCategoryService.getSubCategoryList();
      // CHANGED: Fix filter default and compare properly
      setSubCategoryDropdown(
        response?.data?.filter((f) => f.categoryId === (categoryId ?? 0)) ?? []
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getAssetSpecificationLst = async () => {
    try {
      refAssetSpecDataGrid?.current?.instance?.beginCustomLoading();
      const response = await assetSpecService.getassetSpecList();
      setAssetSpecficationDataSourceLst(response?.data ?? []);
    } catch (error) {
      console.log(error);
    } finally {
      try {
        refAssetSpecDataGrid?.current?.instance?.endCustomLoading();
      } catch (err) {}
    }
  };

  const getAssetSpecificationSubLst = async (assetSpecId) => {
    try {
      refAssetSpecSubDataGrid?.current?.instance?.beginCustomLoading();
      const response = await assetSpecService.getSpecsub(assetSpecId);
      setState((prev) => ({
        ...prev,
        assetSpecificationSubLst: response?.data ?? [],
      }));
    } catch (error) {
      console.log(error);
    } finally {
      try {
        refAssetSpecSubDataGrid?.current?.instance?.endCustomLoading();
      } catch (err) {}
    }
  };

  const isValidData = () => {
    let isValid = true;
    try {
      let err = { ...errors };
      err.categoryValidation = "";
      err.subCategoryValidation = "";
      if (state.categoryId === 0) {
        err.categoryValidation = "Please select category.";
        isValid = false;
      }
      if (state.subCategoryId === 0) {
        err.subCategoryValidation = "Please select sub category.";
        isValid = false;
      }
      setErrors(err);
    } catch (error) {
      console.log(error);
    }
    return isValid;
  };

  const handleOnSaveAssetSpecification = async (e) => {
    e.preventDefault();
    try {
      if (isValidData()) {
        let confirmationQuestion = `<span style='font-size:16px;'>Are You Sure Want To ${
          state.assetSpecId === 0 ? `Add` : `Update`
        } Asset Specification?</span>`;
        let isConfirmed = await askConfirmation(
          confirmationQuestion,
          "Yes",
          "No"
        );
        if (!isConfirmed) return;
        setState((prev) => ({ ...prev, isSubmitted: true }));
        let body = {
          assetSpecId: state.assetSpecId,
          categoryId: state.categoryId,
          subCategoryId: state.subCategoryId,
          assetSpecificationSubs: state.assetSpecificationSubLst,
          ModifiedBy: user?.userId,
        };
        const response = await assetSpecService.addAssetspec(body);
        await showResponseMessage(response);
        setState((prev) => ({ ...prev, isSubmitted: false }));
        if (response?.isSuccess) {
          handleOnCloseSaveAssetSpecificationModal();
          await getAssetSpecificationLst();
        }
      }
    } catch (error) {
      setState((prev) => ({ ...prev, isSubmitted: false }));
      console.log(error);
    }
  };

  const handleOnViewSaveAssetSpecificationModal = async (e, data) => {
    e.preventDefault();
    try {
      if (data?.id > 0) {
        await getSubCategoryDropdown(data?.categoryId);
      }
      setState((prev) => ({
        ...prev,
        isSaveAssetSpecificationModalOpen: true,
        assetSpecId: data?.id || 0,
        categoryId: data?.categoryId ?? 0,
        subCategoryId: data?.subCategoryId ?? 0,
        rowData: data,
        isViewOnly: true,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnOpenSaveAssetSpecificationModal = async (e, data) => {
    e.preventDefault();
    try {
      if (data?.id > 0) {
        await getSubCategoryDropdown(data?.categoryId);
      }
      setState((prev) => ({
        ...prev,
        isSaveAssetSpecificationModalOpen: true,
        assetSpecId: data?.id || 0,
        categoryId: data?.categoryId ?? 0,
        subCategoryId: data?.subCategoryId ?? 0,
        rowData: data,
        isViewOnly: false,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnCloseSaveAssetSpecificationModal = () => {
    try {
      setState((prev) => ({
        ...prev,
        isSaveAssetSpecificationModalOpen: false,
        assetSpecId: 0,
        categoryId: 0,
        subCategoryId: 0,
        rowData: null,
        assetSpecificationSubLst: [],
        isViewOnly: false,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  // helper to add a new sub row (keeps logic DRY)
  const addNewSubRow = () => {
    try {
      setState((prev) => {
        const existingList = prev.assetSpecificationSubLst || [];
        const maxSrNo =
          existingList.length > 0 ? Math.max(...existingList.map((item) => item.srNo || 0)) : 0;
        const newRow = { tempId: Date.now(), assetSpecSubId: 0, srNo: maxSrNo + 1, assetSpecSubName: "" };
        return { ...prev, assetSpecificationSubLst: [...existingList, newRow] };
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="content">
        <div className="card">
          <div className="card-body">
            <DataGrid
              ref={refAssetSpecDataGrid}
              keyExpr="id"
              dataSource={assetSpecficationDataSourceLst}
              showColumnLines={false}
              showRowLines={true}
              showBorders={true}
              allowColumnResizing={false}
              allowColumnReordering={true}
              columnAutoWidth={true}
              onToolbarPreparing={(e) => {
                // CHANGED: use Template items like before; add Add button only if canAdd
                e.toolbarOptions.items.unshift(
                  {
                    location: "before",
                    template: "toolbarAddTemplate",
                  },
                  {
                    location: "before",
                    template: "toolbarRefreshTemplate",
                  }
                );
              }}
            >
              <Column
                dataField="id"
                width={60}
                caption=""
                allowExporting={false}
                allowFiltering={false}
                allowSearch={false}
                cellRender={(data) => {
                  return (
                    <span>
                      <Icon
                        className="iconify-size mr-1"
                        title="View"
                        tooltip="View"
                        icon="solar:document-text-outline"
                        style={{ cursor: "pointer" }}
                        onClick={async (e) => {
                          await handleOnViewSaveAssetSpecificationModal(e, data?.data);
                        }}
                      />
                      {/* CHANGED: show edit icon only when canEdit is true */}
                      {canEdit && (
                        <Icon
                          className="iconify-size mr-1"
                          title="Edit"
                          tooltip="Edit"
                          icon="solar:pen-line-duotone"
                          style={{ cursor: "pointer" }}
                          onClick={async (e) => {
                            await handleOnOpenSaveAssetSpecificationModal(e, data?.data);
                          }}
                        />
                      )}

                      {/* CHANGED: delete icon shown only when canDelete true */}
                      {canDelete && (
                        <Icon
                          className="iconify-size"
                          title="Delete"
                          tooltip="Delete"
                          icon="fluent:delete-32-regular"
                          style={{ cursor: "pointer" }}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              let confirmationQuestion = `Are You Sure Want To Delete Asset Specification?`;
                              let isConfirmed = await askConfirmation(confirmationQuestion, "Yes", "No");
                              if (!isConfirmed) return;
                              // TODO: call delete API when available, e.g. assetSpecService.deleteAssetSpec(data?.data?.id)
                              // const response = await assetSpecService.deleteAssetSpec(data?.data?.id);
                              // await showResponseMessage(response);
                              // if (response?.isSuccess) await getAssetSpecificationLst();
                            } catch (error) {
                              console.log(error);
                            }
                          }}
                        />
                      )}
                    </span>
                  );
                }}
              />
              <Column dataField="categoryName" caption="Category" allowSearch={true} />
              <Column dataField="subCategoryName" caption="Sub Category" allowSearch={true} />
              <Column dataField="specifications" caption="Specifications" allowSearch={true} />

              <Template
                name="toolbarAddTemplate"
                render={() => {
                  if (!canAdd) return null; // CHANGED: don't render if user can't add
                  return (
                    <>
                      <button
                        style={{ cursor: "pointer" }}
                        type="button"
                        title="Add Asset Specification"
                        className="btn btn-primary btn-sm mr-1"
                        onClick={() => {
                          try {
                            setState((prev) => ({
                              ...prev,
                              isSaveAssetSpecificationModalOpen: true,
                            }));
                          } catch (error) {
                            console.log(error);
                          }
                        }}
                      >
                        <span title="Add Asset Specification">
                          <i className="fas fa-plus"></i>&nbsp;&nbsp;Add
                        </span>
                      </button>
                    </>
                  );
                }}
              />

              <Template
                name="toolbarRefreshTemplate"
                render={() => {
                  return (
                    <>
                      <button
                        style={{ cursor: "pointer" }}
                        type="button"
                        title="Refresh"
                        className="btn btn-outline-primary btn-sm"
                        onClick={async () => {
                          try {
                            await getAssetSpecificationLst();
                          } catch (error) {
                            console.log(error);
                          }
                        }}
                      >
                        <span title="Refresh">
                          <i className="fas fa-sync-alt"></i>&nbsp;&nbsp;Refresh
                        </span>
                      </button>
                    </>
                  );
                }}
              />

              <HeaderFilter visible={false} />
              <SearchPanel visible={true} highlightCaseSensitive={true} width={240} placeholder="Search..." />
              <Paging defaultPageSize={15} />
              <Pager displayMode="compact" showInfo={true} visible={true} />
              <Export enabled={true} fileName="Asset Specification" />
            </DataGrid>
          </div>
        </div>
      </div>

      {/* For Adding/Updating/Viewing Asset Specification */}
      <Modal
        ariaHideApp={false}
        isOpen={state.isSaveAssetSpecificationModalOpen}
        onAfterOpen={async () => {
          await getCategoryDropdown();
          await getAssetSpecificationSubLst(state.assetSpecId);
          let err = { ...errors };
          err.categoryValidation = "";
          err.subCategoryValidation = "";
          setErrors(err);
        }}
        className={"react-modal custom-modal-medium"}
        shouldCloseOnOverlayClick={false}
        contentLabel="Add/Edit Asset Specification"
      >
        <div className="modal-header">
          <h6 className="modal-title">
            {state.assetSpecId > 0 && !state.isViewOnly
              ? `Update Asset Specification`
              : state.assetSpecId > 0 && state.isViewOnly
              ? `View Asset Specification`
              : state.assetSpecId === 0 && !state.isViewOnly
              ? `Add Asset Specification`
              : ``}
          </h6>
          <button type="button" className="close" onClick={() => handleOnCloseSaveAssetSpecificationModal()}>
            <span aria-hidden="true">x</span>
          </button>
        </div>

        <div className="modal-body">
          <div className="row">
            <div className="col-12 col-lg-6">
              <div className="form-group mb-2">
                <label>
                  Category&nbsp;<span className="text-danger">*</span>
                </label>
                <SelectBox
                  dataSource={categoryDropdown}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select category"
                  name="categoryDropdown"
                  displayExpr="name"
                  valueExpr="id"
                  value={state.categoryId}
                  defaultValue={state.categoryId}
                  showClearButton={true}
                  disabled={state.isViewOnly}
                  searchEnabled={true}
                  onSelectionChanged={async (e) => {
                    let err = { ...errors };
                    err.categoryValidation = "";
                    setErrors(err);
                    setState((prev) => ({ ...prev, categoryId: e?.selectedItem?.id ?? 0, subCategoryId: 0 }));
                    setSubCategoryDropdown([]);
                    if (e?.selectedItem?.id > 0) {
                      await getSubCategoryDropdown(e?.selectedItem?.id);
                    }
                  }}
                />
                {errors.categoryValidation && <div className="text-danger">{errors.categoryValidation}</div>}
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="form-group mb-2">
                <label>
                  Sub Category&nbsp;<span className="text-danger">*</span>
                </label>
                <SelectBox
                  dataSource={subCategoryDropdown}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select sub category"
                  name="subCategoryDropdown"
                  displayExpr="name"
                  valueExpr="id"
                  value={state.subCategoryId}
                  defaultValue={state.subCategoryId}
                  showClearButton={true}
                  disabled={state.isViewOnly}
                  searchEnabled={true}
                  onSelectionChanged={async (e) => {
                    let err = { ...errors };
                    err.subCategoryValidation = "";
                    setErrors(err);
                    setState((prev) => ({ ...prev, subCategoryId: e?.selectedItem?.id ?? 0 }));
                  }}
                />
                {errors.subCategoryValidation && <div className="text-danger">{errors.subCategoryValidation}</div>}
              </div>
            </div>
          </div>

          <hr />

          <div className="row">
            <div className="col-12 col-lg-12">
              <div className="form-group mb-2">
                <DataGrid
                  ref={refAssetSpecSubDataGrid}
                  dataSource={state.assetSpecificationSubLst}
                  showColumnLines={false}
                  showRowLines={true}
                  showBorders={true}
                  allowColumnResizing={false}
                  allowColumnReordering={true}
                  columnAutoWidth={true}
                  onToolbarPreparing={(e) => {
                    e.toolbarOptions.items.unshift({
                      location: "after",
                      template: "toolbarAddAssetSpecSubTemplate",
                    });
                  }}
                  height={250}
                >
                  {/* CHANGED: wire editing permissions to Editing component */}
                  <Editing
                    mode="cell"
                    allowUpdating={canEdit && !state.isViewOnly}
                    allowAdding={false}
                    allowDeleting={canDelete && !state.isViewOnly}
                    useIcons={true}
                    startEditAction="click"
                  />
                  <Column width={"auto"} dataField="srNo" caption="Seq. No." alignment={"center"} allowEditing={false} allowSorting={true} allowSearch={false} />
                  <Column dataField="assetSpecSubName" caption="Name" allowSorting={true} allowSearch={true} allowEditing={!state.isViewOnly} />

                  {/* CHANGED: Add Row button hidden when cannot add */}
                  <Template
                    name="toolbarAddAssetSpecSubTemplate"
                    render={() => {
                      if (!canAdd || state.isViewOnly) return null;
                      return (
                        <>
                          <button
                            style={{ cursor: "pointer" }}
                            type="button"
                            title="Add Row"
                            className="btn btn-primary btn-sm mr-1"
                            onClick={(e) => {
                              e.preventDefault();
                              addNewSubRow();
                            }}
                          >
                            <span title="Add Row">
                              <i className="fas fa-plus"></i>&nbsp;&nbsp;Add Row
                            </span>
                          </button>
                        </>
                      );
                    }}
                  />

                  <SearchPanel visible={true} highlightCaseSensitive={true} width={240} placeholder="Search..." />
                  <Scrolling mode={"virtual"} />
                </DataGrid>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={async (e) => {
              await handleOnSaveAssetSpecification(e);
            }}
            hidden={state.isViewOnly}
            disabled={state.isSubmitted}
          >
            <span title="Submit">
              <i className={`fas fa-${state.isSubmitted ? "spinner fa-spin" : "check"}`} />
              &nbsp;&nbsp;Submit
            </span>
          </button>
          &nbsp;&nbsp;
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            title="Cancel"
            onClick={() => {
              handleOnCloseSaveAssetSpecificationModal();
            }}
          >
            <i className="fas fa-times"></i>&nbsp;&nbsp;Cancel
          </button>
        </div>
      </Modal>
    </>
  );
}
